<template>
  <div class="app">
    <!-- 랜딩페이지는 전체 화면 -->
    <template v-if="currentPage === 'home'">
      <router-view></router-view>
    </template>
    
    <!-- 로그인/회원가입 페이지는 전체 화면 -->
    <template v-else-if="currentPage === 'login' || currentPage === 'signup'">
      <router-view></router-view>
    </template>

    <!-- 회의 화면에서는 전체 화면 -->
    <template v-else-if="currentPage === 'meeting-room'">
      <router-view></router-view>
    </template>
    
    <!-- 모바일 페이지는 전체 화면 (사이드바 없음) -->
    <template v-else-if="isMobilePage">
      <router-view></router-view>
    </template>
    
    <!-- 데스크톱 환경의 메인 페이지들은 좌우 분할 레이아웃 -->
    <template v-else>
      <div class="app-layout">
        <MainNavigation :current-page="currentPage" @sidebar-toggle="handleSidebarToggle" />
        <main class="main-content" :style="{ marginLeft: sidebarMargin, paddingLeft: '1rem' }">
          <router-view></router-view>
        </main>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import MainNavigation from '@/components/MainNavigation.vue'
import { isMobile } from '@/utils/deviceDetection'

const route = useRoute()
const currentPage = computed(() => route.name)

const isMobileView = ref(false)
const isSidebarExpanded = ref(false)

// 모바일 페이지 여부 확인 (라우트 이름 기반)
const isMobilePage = computed(() => {
  const mobileRoutes = ['mobile-dashboard', 'MobileMeeting']
  return mobileRoutes.includes(currentPage.value)
})

// Sidebar margin calculation
const sidebarMargin = computed(() => {
  return isSidebarExpanded.value ? '16rem' : '6rem' // w-60 + 1rem padding, w-20 + 1rem padding
})

// Handle sidebar toggle
const handleSidebarToggle = (expanded) => {
  isSidebarExpanded.value = expanded
}

onMounted(() => {
  // 모바일 환경 감지
  isMobileView.value = isMobile()
  
  // 초기 사이드바 상태 확인
  const checkInitialSidebarState = () => {
    const sidebarWrapper = document.querySelector('.sidebar-wrapper')
    if (sidebarWrapper) {
      isSidebarExpanded.value = sidebarWrapper.classList.contains('expanded')
    }
  }
  
  // DOM이 준비된 후 사이드바 상태 확인
  setTimeout(() => {
    checkInitialSidebarState()
  }, 50)
  
  // 화면 크기 변경 시 감지
  window.addEventListener('resize', () => {
    isMobileView.value = isMobile()
  })
})

</script>

<style>
@import './assets/main.css';

:root {
  --app-height: 100%;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
}

body {
  overflow-x: hidden;
  line-height: 1;
  top: 0 !important;
}

.app {
  min-height: 100vh;
  min-height: var(--app-height);
  width: 100%;
  position: relative;
  background: var(--background);
  color: var(--foreground);
  overflow-x: hidden;
}

/* 좌우 분할 레이아웃 (데스크톱 전용) */
.app-layout {
  display: flex;
  min-height: 100vh;
  width: 100%;
}

.main-content {
  flex: 1;
  min-height: 100vh;
  overflow-x: hidden;
}

/* 모바일 페이지는 전체 화면 */
.app > router-view:only-child {
  width: 100%;
  min-height: 100vh;
}

/* 구글 번역 위젯 드롭다운 강제 표시 */
.goog-te-combo {
  z-index: 900 !important;
  position: relative !important;
}

.goog-te-menu {
  z-index: 900 !important;
  position: absolute !important;
}

.goog-te-menu-frame {
  z-index: 900 !important;
  position: absolute !important;
}

/* 번역 위젯 전체 컨테이너 */
.translate-selector {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 900 ; 
  /* 디버깅용 스타일 추가 */
}

/* 구글 번역 위젯 루트 요소들 */
#google_translate_element {
  z-index: 900 !important;
}

#google_translate_element * {
  z-index: 900 !important;
}
.goog-te-banner-frame.skiptranslate,
.goog-te-banner-frame,
.skiptranslate,
.goog-tooltip,
.goog-tooltip:hover,
.goog-text-highlight {
  display: none !important;
}

</style>